import { formatTime, getPhaseTypeLabel, getPhaseTypeColor } from '../lib/utils';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, User, FileText, X } from 'lucide-react';

const getEmbedUrl = (url) => {
  if (!url) return url;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
};

const isDirectVideo = (url) => {
  if (!url) return false;
  return ['.mp4', '.webm', '.ogg', '.m3u8', '.mpd'].some(ext => url.toLowerCase().includes(ext));
};

// Bloque de recursos de pantallas para la fase actual — previsualización inline
const ScreensBlock = ({ currentPhase, screens }) => {
  if (!screens || screens.length === 0) return null;

  const screenValues = currentPhase?.screen_values || {};
  const screensWithContent = screens.filter(s => screenValues[String(s.id)]?.content_url);

  if (screensWithContent.length === 0) return null;

  return (
    <div className="border-b border-border overflow-y-auto" style={{ maxHeight: '320px' }}>
      <div className="px-4 pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider sticky top-0 bg-card">
        Recursos de fase
      </div>
      {screensWithContent.map(screen => {
        const val = screenValues[String(screen.id)];
        const contentType = val?.content_type || 'image';
        const contentUrl = val?.content_url;
        return (
          <div key={screen.id} className="mb-2">
            <div className="px-4 py-1 text-[11px] font-medium text-muted-foreground">{screen.name}</div>
            {contentType === 'image' ? (
              <img
                src={contentUrl}
                alt={screen.name}
                className="w-full max-h-44 object-contain bg-black/30"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : contentType === 'video' ? (
              isDirectVideo(contentUrl) ? (
                <video
                  src={contentUrl}
                  className="w-full aspect-video bg-black"
                  controls
                  preload="metadata"
                />
              ) : (
                <div className="aspect-video w-full">
                  <iframe
                    src={getEmbedUrl(contentUrl)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={screen.name}
                  />
                </div>
              )
            ) : contentType === 'audio' ? (
              <div className="px-4 pb-2">
                <audio controls className="w-full" src={contentUrl} preload="metadata">
                  Tu navegador no soporta audio.
                </audio>
              </div>
            ) : (
              <div className="px-4 pb-2">
                <iframe
                  src={contentUrl}
                  className="w-full rounded border border-border"
                  style={{ height: '160px' }}
                  title={screen.name}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const TimerPanel = ({ currentPhase, nextPhase, phaseElapsed, timerRunning, speakers, screens = [], onClose }) => {
  const speaker = currentPhase ? speakers.find(s => s.id === currentPhase.speaker_id) : null;
  const nextSpeaker = nextPhase ? speakers.find(s => s.id === nextPhase.speaker_id) : null;

  const remaining = currentPhase ? currentPhase.duration_seconds - phaseElapsed : 0;
  const isOvertime = remaining < 0;
  const progress = currentPhase ? Math.min(100, (phaseElapsed / currentPhase.duration_seconds) * 100) : 0;

  return (
    <div className="w-80 border-l border-border bg-card flex flex-col h-full" data-testid="timer-panel">
      {/* Panel header with close button */}
      {onClose && (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border flex-shrink-0">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Panel de fase</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-6 w-6">
            <X className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
      {/* Current phase timer */}
      <div className="p-6 border-b border-border">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Fase actual</div>

        {currentPhase ? (
          <>
            <div className={`timer-display text-5xl font-bold text-center mb-4 ${
              isOvertime ? 'text-red-500' : 'text-foreground'
            }`}>
              {isOvertime && '+'}
              {formatTime(Math.abs(remaining))}
            </div>

            {/* Progress bar */}
            <div className="relative h-2 bg-muted rounded-full overflow-hidden mb-4">
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-500 ${
                  isOvertime ? 'bg-red-500' : progress > 80 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge
                  className="text-[10px] uppercase"
                  style={{ backgroundColor: getPhaseTypeColor(currentPhase.phase_type) }}
                >
                  {getPhaseTypeLabel(currentPhase.phase_type)}
                </Badge>
                {timerRunning && (
                  <Badge variant="destructive" className="text-[10px] uppercase animate-pulse">
                    En vivo
                  </Badge>
                )}
              </div>

              <h3 className="font-heading text-lg font-bold">{currentPhase.name}</h3>

              {currentPhase.description && (
                <p className="text-sm text-muted-foreground">{currentPhase.description}</p>
              )}

              {speaker && (
                <div className="flex items-center gap-2 text-sm">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                    {speaker.initials}
                  </div>
                  <span>{speaker.name}</span>
                </div>
              )}

              {currentPhase.notes && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 rounded p-2">
                  <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{currentPhase.notes}</span>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Presiona Play para iniciar</p>
          </div>
        )}
      </div>

      {/* Screen resources preview */}
      {currentPhase && (
        <ScreensBlock currentPhase={currentPhase} screens={screens} />
      )}

      {/* Next phase preview */}
      <div className="p-6 flex-1">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Siguiente</div>

        {nextPhase && nextPhase.id !== currentPhase?.id ? (
          <div className="space-y-2">
            <Badge
              variant="outline"
              className="text-[10px] uppercase"
              style={{ borderColor: getPhaseTypeColor(nextPhase.phase_type), color: getPhaseTypeColor(nextPhase.phase_type) }}
            >
              {getPhaseTypeLabel(nextPhase.phase_type)}
            </Badge>

            <h4 className="font-medium">{nextPhase.name}</h4>

            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {formatTime(nextPhase.duration_seconds)}
              </div>

              {nextSpeaker && (
                <div className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5" />
                  {nextSpeaker.name.split(' ')[0]}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            {currentPhase ? 'Última fase del evento' : 'No hay fases programadas'}
          </div>
        )}
      </div>

      {/* Keyboard hints */}
      <div className="p-4 border-t border-border text-xs text-muted-foreground">
        <div className="flex items-center justify-between mb-1">
          <span>Play/Pause</span>
          <kbd className="kbd">Space</kbd>
        </div>
        <div className="flex items-center justify-between mb-1">
          <span>Anterior</span>
          <kbd className="kbd">←</kbd>
        </div>
        <div className="flex items-center justify-between">
          <span>Siguiente</span>
          <kbd className="kbd">→</kbd>
        </div>
      </div>
    </div>
  );
};

export default TimerPanel;
